/**
 * Dashboard Page
 * Stats cards, pipeline overview, and recent leads.
 */

import React, { useEffect, useState } from 'react';
import { tintOf } from '../lib/theme';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useApiClient } from '../hooks/useApi';
import { STAGES, stageColor, stageBg } from '../lib/stages';
import { formatDate, formatMoney } from '../lib/utils';
import { formatJobNo } from '../lib/jobNumber';
import { repName } from './ClientCells';
import { userTz, todayInTz, addDaysStr } from '../lib/time';
import { WorldClock } from './WorldClock';
import { DayTimetable } from './DayTimetable';
import { ActivityStats } from './ActivityStats';
import { Icons } from '../lib/icons';
import { useLeads } from '../contexts/LeadsContext';
import { leadsOfFollowUps } from '../lib/followUpLeads';

interface DashboardProps {
  onSelectLead?: (leadId: string) => void;
  onOpenFollowUps?: (when: string) => void; // N4b: Tomorrow count → Follow-Ups
}

export function Dashboard({ onSelectLead, onOpenFollowUps }: DashboardProps) {
  const { token, user } = useAuth();
  const client = useApiClient();

  // Whether the compact day timetable has anything on it (a lone reminder counts).
  // Lets us keep the "all caught up" panel for follow-ups without hiding the day.
  const [ttHasContent, setTtHasContent] = useState(false);

  // Live poll (~3s) so the "today" panel + counts stay in step with the badges.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 3000);
    return () => window.clearInterval(id);
  }, []);

  // EVERY FIGURE FROM THE SERVER (GET /dashboard/summary, paging 3/4): exact
  // counts per stage, the three money cards and the newest leads. This page
  // used to load every lead and every estimate and count arrays here — both
  // lists stopped at 1,000 rows. Refetched whenever a lead changes.
  const { version } = useLeads();
  const { data: summary, loading, error, refetch } = useApi(
    async (api) => (await api.getDashboardSummary()).data ?? null,
    { skip: !token, dependencies: [version] }
  );
  const { data: followupsData, refetch: refetchFollowups } = useApi<any[]>(
    async (api) => (await api.getFollowUps()).data || [],
    { skip: !token, dependencies: [tick] }
  );
  const { data: usersData } = useApi<any[]>(async (api) => (await api.getUsers()).data || [], { skip: !token });
  const tz = userTz(user);
  const { data: badges } = useApi<{ notificationsUnread: number } | null>(
    async (api) => (await api.getBadges(todayInTz(tz))).data ?? null,
    { skip: !token, dependencies: [tick, tz] }
  );

  if (loading && !summary) return <div style={{ padding: '40px', color: 'var(--crm-text)' }}>Loading dashboard…</div>;

  const followups = followupsData || [];
  const users = usersData || [];
  // Each follow-up carries its lead (job number, name) — the timetable's labels.
  const leadById = leadsOfFollowUps(followups);
  const recent: any[] = summary?.recent ?? [];

  const t = todayInTz(tz);
  const tmr = addDaysStr(t, 1);
  const mine = followups.filter((f) => f.assigned_to_user_id === user?.id && f.completed !== true);
  const todayMine = mine.filter((f) => f.follow_up_date === t);
  const tomorrowCount = mine.filter((f) => f.follow_up_date === tmr).length; // N4b: timed + untimed
  const todayCount = todayMine.length;
  const unread = badges?.notificationsUnread ?? 0;
  const caughtUp = todayCount === 0 && unread === 0;
  // "All caught up" is about follow-ups + notifications — it must NOT hide a day
  // that still has a reminder on it. Show the panel only when the grid is empty.
  const showCaughtUpPanel = caughtUp && !ttHasContent;

  // Dashboard drag-to-reschedule (compact variant) goes through the same path.
  const reschedule = async (fuId: string, patch: { follow_up_date: string; follow_up_time: string | null }) => {
    await client.updateFollowUp(fuId, patch);
    await refetchFollowups();
  };

  /**
   * THREE FIGURES, AND EVERY DOLLAR SITS ON EXACTLY ONE OF THEM — worked out on
   * the server (routes/dashboard.ts), by the rules that were here:
   *
   * Pipeline value — In Progress or Opportunity, AND the estimate has actually
   *   been emailed to the client. A quote nobody has seen is a draft. New Lead
   *   is out (nothing quoted yet); Lost and Bad Lead are out by stage.
   * Booked revenue — stage Booked. Won work, not yet invoiced.
   * Outstanding invoices — balance still due on Invoiced leads, from each
   *   invoice's own snapshot (the locked number, part-payments applied).
   *
   * Disjoint by stage, so nothing is counted twice and — with Closed reported
   * as revenue in Accounting — nothing invoiced-but-unpaid falls off.
   */
  const pipelineValue = summary?.pipelineValue ?? 0;
  const bookedRevenue = summary?.bookedRevenue ?? 0;
  const outstandingInvoices = summary?.outstanding ?? 0;

  const stats = {
    totalLeads: summary?.total ?? 0,
    unclaimedLeads: summary?.unclaimed ?? 0,
  };

  const stageCounts: Record<string, number> = {};
  STAGES.forEach((s) => {
    stageCounts[s] = summary?.stageCounts?.[s] ?? 0;
  });

  return (
    <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
      <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '14px' }}>Dashboard</h1>

      {/* World clock strip + per-user timezone (N2b) */}
      <WorldClock />

      {/* ===== Today command center (N2) ===== */}
      <div style={{ background: caughtUp ? 'linear-gradient(135deg,var(--crm-green-tint),var(--crm-green-tint))' : 'var(--crm-panel)', border: `1px solid ${caughtUp ? 'var(--crm-green-soft-line)' : 'var(--crm-line)'}`, borderRadius: '12px', padding: '16px 18px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showCaughtUpPanel ? 0 : '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)' }}>
            Today · <span style={{ color: 'var(--crm-amber)' }}>{todayCount}</span> {todayCount === 1 ? 'follow-up' : 'follow-ups'} due
            {unread > 0 && <span style={{ color: 'var(--crm-muted)', fontWeight: 500 }}> · {unread} unread notification{unread === 1 ? '' : 's'}</span>}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--crm-dim)', fontFamily: "'DM Mono', monospace" }}>{formatDate(t)}</div>
        </div>

        {/* Day timetable (compact variant) — replaces the old "Timed calls" list.
            Always mounted so it fetches reminders + reports content; only visually
            hidden when the day is empty AND you're caught up (panel shows instead). */}
        <div style={{ display: showCaughtUpPanel ? 'none' : 'block' }}>
          <DayTimetable
            date={t}
            followUps={todayMine}
            users={users}
            leadById={leadById}
            viewer={user}
            tz={tz}
            compact
            editable
            onReschedule={reschedule}
            onContentChange={setTtHasContent}
            onSelectLead={onSelectLead}
          />
        </div>

        {showCaughtUpPanel && (
          <div style={{ textAlign: 'center', padding: '18px 0 6px' }}>
            <div style={{ marginBottom: '6px', color: 'var(--crm-green)' }}><Icons.caughtUp size={30} /></div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--crm-green)' }}>You're all caught up for today</div>
            <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginTop: '3px' }}>No follow-ups due and no unread notifications. Nice work.</div>
          </div>
        )}

        {/* Tomorrow — a count click-through to Follow-Ups (tomorrow selected) */}
        {tomorrowCount > 0 && (
          <div
            onClick={() => onOpenFollowUps?.(tmr)}
            style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--crm-line)', display: 'flex', alignItems: 'center', gap: '8px', cursor: onOpenFollowUps ? 'pointer' : 'default' }}
          >
            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--crm-dim)', fontWeight: 700 }}>Tomorrow</span>
            <span style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
              <span style={{ color: 'var(--crm-amber)', fontWeight: 700 }}>{tomorrowCount}</span> {tomorrowCount === 1 ? 'follow-up' : 'follow-ups'}
            </span>
            {onOpenFollowUps && <span style={{ fontSize: '11px', color: 'var(--crm-accent)' }}>View →</span>}
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 14px',
            background: 'var(--crm-red-tint)',
            border: '1px solid var(--crm-red-soft-line)',
            borderRadius: '8px',
            color: 'var(--crm-red)',
            fontSize: '13px',
          }}
        >
          {error}{' '}
          <button
            onClick={refetch}
            style={{ marginLeft: 8, background: 'none', border: '1px solid var(--crm-red-soft-line)', color: 'var(--crm-red)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Stats Grid — the three money figures first, then the two counts.
          auto-fit rather than a fixed five columns so the row reflows instead
          of crushing the numbers on a narrow window; 21px 'DM Mono' needs
          about 150px to stay readable, which is where the minimum comes from. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(176px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        {[
          { label: 'Pipeline value', value: formatMoney(pipelineValue), color: 'var(--crm-amber)', Icon: Icons.money, title: 'In Progress + Opportunity, where the estimate has been emailed to the client' },
          { label: 'Booked revenue', value: formatMoney(bookedRevenue), color: 'var(--crm-green)', Icon: Icons.check, title: 'Estimate totals for leads in Booked — won, not yet invoiced' },
          { label: 'Outstanding invoices', value: formatMoney(outstandingInvoices), color: 'var(--crm-teal)', Icon: Icons.money, title: 'Balance still due on invoiced jobs, from each invoice snapshot' },
          { label: 'Unclaimed', value: stats.unclaimedLeads, color: 'var(--crm-red)', Icon: Icons.star, title: 'New Leads with no rep assigned' },
          { label: 'Total clients', value: stats.totalLeads, color: 'var(--crm-accent)', Icon: Icons.clients, title: 'Every lead on file, every stage' },
        ].map((stat, i) => (
          <div
            key={i}
            title={stat.title}
            style={{
              background: 'var(--crm-panel)',
              border: '1px solid var(--crm-line)',
              borderRadius: '10px',
              padding: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '9px',
                background: `${tintOf(stat.color, 13)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.color,
              }}
            >
              <stat.Icon size={18} aria-hidden="true" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '21px', fontWeight: 700, fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stat.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline */}
      {/* Eight stages now. auto-fit for the same reason as the cards above: a
          hard column count is what made this need editing when the pipeline
          gained a stage. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: '6px', marginBottom: '14px' }}>
        {STAGES.map((stage) => (
          <div
            key={stage}
            style={{
              background: 'var(--crm-panel)',
              border: '1px solid var(--crm-line)',
              borderTop: `2px solid ${stageColor(stage)}`,
              borderRadius: '8px',
              padding: '9px 8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '10px', color: 'var(--crm-muted)', marginBottom: '3px', fontWeight: 500 }}>{stage}</div>
            <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: "'DM Mono', monospace", color: stageColor(stage) }}>
              {stageCounts[stage]}
            </div>
          </div>
        ))}
      </div>

      {/* Recent Leads Table */}
      <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden' }}>
        <div
          style={{
            padding: '11px 14px',
            borderBottom: '1px solid var(--crm-line)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Recent leads
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--crm-panel)' }}>
              {['Job #', 'Client', 'Stage', 'Type', 'Date', 'Rep', 'Value'].map((h) => (
                <th
                  key={h}
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: 'var(--crm-dim)',
                    textTransform: 'uppercase',
                    padding: '8px 12px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--crm-line)',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recent.map((lead) => {
              // null = not quoted (an em-dash), never $0.00.
              const value: number | null = lead.value ?? null;
              return (
                <tr
                  key={lead.id}
                  onClick={() => onSelectLead?.(lead.id)}
                  title={onSelectLead ? `Open ${lead.first_name} ${lead.last_name}` : undefined}
                  style={{ borderBottom: '1px solid var(--crm-line)', cursor: onSelectLead ? 'pointer' : 'default' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--crm-panel-2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '9px 12px', fontSize: '11px', color: 'var(--crm-dim)', fontFamily: "'DM Mono', monospace" }}>{formatJobNo(lead)}</td>
                  <td style={{ padding: '9px 12px', fontSize: '12px', fontWeight: 500 }}>
                    {lead.first_name} {lead.last_name}
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: '12px' }}>
                    <span style={{ background: stageBg(lead.stage), color: stageColor(lead.stage), padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>
                      {lead.stage}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: '11px', color: 'var(--crm-muted)' }}>{lead.move_type || '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: '11px', color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace" }}>{lead.move_date ? formatDate(lead.move_date) : '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: '11px', color: 'var(--crm-muted)' }}>{repName(users, lead.assigned_rep_id) ?? '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: '12px', color: value ? 'var(--crm-green)' : 'var(--crm-dim)', fontFamily: "'DM Mono', monospace" }}>
                    {value != null ? formatMoney(value) : '—'}
                  </td>
                </tr>
              );
            })}
            {recent.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--crm-dim)' }}>
                  No leads yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Activity stats (N4c) — same component, compact variant, below everything.
          Owns its own 30s refresh (NOT the 3s dashboard tick), and reuses the
          already-fetched users list so there's no duplicate users request. */}
      <div style={{ marginTop: '14px' }}>
        <ActivityStats compact users={users} />
      </div>
    </div>
  );
}
